import React, { lazy, Suspense } from "react";
import { Constants } from "../../../../common/constants";

import { common_format_utils } from "../../../../common/common_format_utils";
import {
	CustomEmojiData,
	GuildData,
	IGetGuildDefaultOptionsFromDMArgs,
	OptionPreset,
	RawEventRSVPOption,
} from "../../../../common/sesh_types";
import { DashboardUtils } from "../dashboard_utils";
const EmojiPickerForm = lazy(() => import("./emoji_picker"));
import { NumberInputForm } from "./number_input";
import { TextInputForm } from "./text_input";
import { ClipLoader } from "react-spinners";
import Select from "react-select";
import { InfoTooltip } from "./improved_components/tooltips/info_tooltip";
import { ModifySettingsButton } from "./improved_components/base_components/modify_settings_button";
import { SettingsBooleanItem } from "./improved_components/settings_items/settings_boolean_item";
import { BoxItem } from "./improved_components/box_item";

interface RSVPOptionsProp {
	name?: string;
	updateFunc: (val: OptionPreset) => void;
	is_premium?: boolean;
	is_edit?: boolean;
	current_preset?: OptionPreset;
	user_presets?: OptionPreset[];
	user_id?: string;
	custom_emojis?: CustomEmojiData[];
	all_guild_data?: GuildData[];
	dm_data?: IGetGuildDefaultOptionsFromDMArgs;
	access_token: string;
	token_type: string;
	from_event_creation?: boolean;
	ignore_unmount?: boolean;
	save_for_guild_settings_data?: {
		on_click_func: (preset: OptionPreset) => void;
		text: string;
	};
}

interface RSVPOptionsState {
	current_preset: OptionPreset;
	original_current_preset: OptionPreset;
	presets: OptionPreset[];
	saving_state: "Saving" | "Deleting";
}

function clonePreset(preset: OptionPreset) {
	let cloned: OptionPreset = {
		name: preset.name,
		author_id: preset.author_id,
		unsaved_changes: preset.unsaved_changes,
		custom_options: [],
		allow_multiple_rsvps: preset.allow_multiple_rsvps ?? false,
	};

	for (let custom_option of preset.custom_options) {
		let cloned_options: any = {};
		for (let key in custom_option) {
			cloned_options[key] = custom_option[key];
		}
		cloned.custom_options.push(cloned_options);
	}
	return cloned;
}

function createPresetDropdownSeparator(text: string) {
	return { value: null, label: text, isDisabled: true };
}

function createPresetDropdownValue(value: OptionPreset, index: number) {
	return { value: index, label: value.name };
}

export default class RSVPOptionsForm extends React.Component<
	RSVPOptionsProp,
	RSVPOptionsState
> {
	constructor(props) {
		super(props);

		let current_preset: OptionPreset;
		let all_presets: OptionPreset[] = [];
		if (this.props.current_preset) {
			current_preset = clonePreset(this.props.current_preset);

			if (current_preset.author_id !== this.props.user_id) {
				all_presets = [current_preset];
			}
		} else {
			current_preset = clonePreset(
				Constants.WEB_UI_DEFAULT_REACTION_PRESETS[0]
			);
		}

		all_presets =
			Constants.WEB_UI_DEFAULT_REACTION_PRESETS.slice(0).concat(
				all_presets
			);

		if (this.props.user_presets) {
			all_presets = all_presets.concat(this.props.user_presets);
		}

		all_presets = all_presets.filter(function (elem, index, self) {
			return index === self.findIndex((val) => elem.name === val.name);
		});

		this.state = {
			current_preset: current_preset,
			original_current_preset: clonePreset(current_preset),
			presets: all_presets,
			saving_state: null,
		};

		this.handleChange = this.handleChange.bind(this);
		this.handlePresetChange = this.handlePresetChange.bind(this);
		this.onButtonPressed = this.onButtonPressed.bind(this);
		this.onSavePresetPressed = this.onSavePresetPressed.bind(this);
		this.onDeletePresetPressed = this.onDeletePresetPressed.bind(this);
		this.onAddPresetPressed = this.onAddPresetPressed.bind(this);
	}

	// TODO: Storing redundant state on both this form and its parent, fix??
	handleChange(index, event, extra_events?) {
		const value =
			event.target.type === "checkbox"
				? event.target.checked
				: event.target.value;

		this.state.current_preset.custom_options[index][event.target.name] =
			value;

		if (extra_events) {
			for (let event of extra_events) {
				this.state.current_preset.custom_options[index][
					event.target.name
				] = event.target.value;
			}
		}

		this.state.current_preset.unsaved_changes = true;

		this.updateOptions(this.state.current_preset);
	}

	componentDidMount() {
		this.props.updateFunc(this.state.current_preset);
	}

	handlePresetChange(event) {
		this.updateOptions(this.state.presets[event.value], true);
	}

	componentWillUnmount() {
		if (this.props.ignore_unmount) {
			return;
		}
		this.props.updateFunc(null);
	}

	onDelete(index) {
		let new_options = this.state.current_preset.custom_options.filter(
			(val, i) => {
				return index !== i;
			}
		);

		this.state.current_preset.custom_options = new_options;
		this.state.current_preset.unsaved_changes = true;

		this.updateOptions(this.state.current_preset, true);
	}

	updateOptions(new_preset: OptionPreset, switched_presets: boolean = false) {
		let new_state;
		if (switched_presets) {
			new_state = {
				current_preset: clonePreset(new_preset),
				original_current_preset: clonePreset(new_preset),
			};
		} else {
			new_state = {
				current_preset: clonePreset(new_preset),
			};
		}

		this.setState({
			...new_state,
		});
		this.props.updateFunc(new_preset);
	}

	renderPresetDropdown() {
		let defaults = [];
		let available = [];
		let unavailable = [];

		this.state.presets.forEach((value, index) => {
			let dropdownElement = createPresetDropdownValue(value, index);

			if (index < Constants.WEB_UI_DEFAULT_REACTION_PRESETS.length) {
				defaults.push(dropdownElement);
				return;
			}

			available.push(dropdownElement);
		});

		type ChannelDropdown = {
			label: string;
			value: number;
			options?: ChannelDropdown[];
		};

		let result = [createPresetDropdownSeparator("─ Defaults ─")];
		result = result.concat(defaults);
		if (available.length > 0) {
			result = result.concat(
				createPresetDropdownSeparator("─ Presets ─")
			);
			result = result.concat(available);
		}
		if (unavailable.length > 0) {
			result = result.concat(
				createPresetDropdownSeparator("─ Unavailable ─")
			);
			result = result.concat(unavailable);
		}

		const dropdown_options: ChannelDropdown[] = [
			{
				label: "─ Defaults ─",
				value: undefined,
				options: defaults,
			},
			{
				label: "─ Presets ─",
				value: undefined,
				options: available,
			},
			{
				label: "─ Unavailable ─",
				value: undefined,
				options: unavailable,
			},
		];

		return dropdown_options;
	}

	OnEditEmojiTooltip(emoji: RawEventRSVPOption) {
		if (!this.props.is_edit || emoji.just_added) {
			return;
		}

		return (
			<span className="tooltipright">
				Can not change emoji of already made event
			</span>
		);
	}

	ButtonDeleteTooltip(is_default_option) {
		if (!(this.props.is_premium && is_default_option)) {
			return;
		}

		return <span className="tooltiptop">Cannot delete default preset</span>;
	}

	PresetButtonTooltip(content: string) {
		if (!this.props.is_premium) {
			return;
		}

		return <span className="tooltiptop">{content}</span>;
	}

	onAddPresetPressed(event) {
		event.preventDefault();
		event.stopPropagation();

		if (!this.props.user_id) {
			return;
		}

		DashboardUtils.ShowPrompt(
			"New Preset",
			"Enter a name for this preset."
		).then((val) => {
			if (!val.isConfirmed) {
				return;
			}

			const preset_name = val.value;
			if (preset_name.length === 0) {
				DashboardUtils.ShowDefaultErrorAlert(
					"Preset name must not be empty."
				);
				return;
			}

			if (
				this.state.presets.find((value) => value.name === preset_name)
			) {
				DashboardUtils.ShowDefaultErrorAlert(
					"Can not overwrite existing preset. Switch to that preset and save it instead."
				);
				return;
			}

			if (
				preset_name.length >
				Constants.CUSTOM_OPTION_PRESET_NAME_MAX_LENGTH
			) {
				DashboardUtils.ShowDefaultErrorAlert(
					`Preset name can not be more than ${Constants.CUSTOM_OPTION_PRESET_NAME_MAX_LENGTH} characters.`
				);
				return;
			}

			let new_preset: OptionPreset = {
				name: preset_name,
				unsaved_changes: true,
				custom_options: [],
				allow_multiple_rsvps: false,
			};

			let new_preset_list = this.state.presets;

			let index = new_preset_list.findIndex(
				(value) => value.name === new_preset.name
			);

			// duplicate found
			if (index > -1) {
				new_preset_list.splice(index, 1);
			}

			new_preset_list.push(new_preset);

			this.setState({
				current_preset: new_preset,
				original_current_preset: clonePreset(new_preset),
				presets: new_preset_list,
			});
			this.props.updateFunc(new_preset);
		});
	}

	onSavePresetPressed(event) {
		event.preventDefault();
		event.stopPropagation();

		if (!this.props.user_id) {
			return;
		}

		DashboardUtils.ShowPrompt(
			"Saving Preset",
			"Enter a name for this preset.\nPut in the same name to overwrite existing preset.",
			this.state.current_preset.name
		).then((val) => {
			if (!val.isConfirmed) {
				return;
			}

			const preset_name = val.value;

			if (
				Constants.WEB_UI_DEFAULT_REACTION_PRESETS.find(
					(value) => value.name === preset_name
				)
			) {
				DashboardUtils.ShowDefaultErrorAlert(
					"Can not overwrite default presets.\nTry a different name."
				);
				return;
			}

			if (
				preset_name.length >
				Constants.CUSTOM_OPTION_PRESET_NAME_MAX_LENGTH
			) {
				DashboardUtils.ShowDefaultErrorAlert(
					`Preset name can not be more than ${Constants.CUSTOM_OPTION_PRESET_NAME_MAX_LENGTH} characters.`
				);
				return;
			}

			const dupe = common_format_utils.get_duplicate_emojis_in_preset(
				this.state.current_preset
			);
			if (dupe) {
				let error = `Duplicate custom emojis [${dupe}]! Can not use the same emoji for multiple options.`;
				DashboardUtils.ShowDefaultErrorAlert(error);
				return;
			}

			let current_preset = clonePreset(this.state.current_preset);
			current_preset.name = preset_name;

			let body = JSON.stringify({
				user_id: this.props.user_id,
				preset: JSON.stringify(current_preset),
				dm_data: JSON.stringify(this.props.dm_data),
				access_token: this.props.access_token,
				token_type: this.props.token_type,
			});

			this.setState({
				saving_state: "Saving",
			});

			fetch(`/api/save_preset`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: body,
			}).then((response) =>
				response.json().then((preset) => {
					let new_preset_list = this.state.presets;

					let index = new_preset_list.findIndex(
						(value) => value.name === preset.name
					);

					// duplicate found
					if (index > -1) {
						new_preset_list.splice(index, 1);
					} else {
						let old_preset_index = new_preset_list.findIndex(
							(val) => this.state.current_preset.name === val.name
						);

						this.state.original_current_preset.unsaved_changes =
							false;

						new_preset_list[old_preset_index] =
							this.state.original_current_preset;
					}

					new_preset_list.push(preset);

					this.setState({
						current_preset: preset,
						original_current_preset: clonePreset(preset),
						presets: new_preset_list,
						saving_state: null,
					});
					this.props.updateFunc(preset);
				})
			);
		});
	}

	onDeletePresetPressed() {
		event.preventDefault();
		event.stopPropagation();

		DashboardUtils.ShowDeleteConfirmAlert(
			<span>
				Are you sure you want to delete preset{" "}
				<span className="has-text-weight-bold">
					{this.state.current_preset.name}
				</span>
				?
			</span>
		).then((val) => {
			if (!val.isConfirmed) {
				return;
			}

			let req = new XMLHttpRequest(); // new HttpRequest instance
			req.open("POST", "/api/delete_preset");
			req.setRequestHeader("Content-Type", "application/json");

			this.setState({
				saving_state: "Deleting",
			});

			fetch(`/api/delete_preset`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					user_id: this.props.user_id,
					preset_name: this.state.current_preset.name,
					dm_data: JSON.stringify(this.props.dm_data),
				}),
			}).then((val) => this.setState({ saving_state: null }));

			let index = this.state.presets.findIndex((value) => {
				return value.name === this.state.current_preset.name;
			});

			if (index > -1) {
				let new_preset_list = this.state.presets;
				new_preset_list.splice(index, 1);

				let current_preset_index = Math.min(
					index,
					new_preset_list.length - 1
				);
				let current_preset = new_preset_list[current_preset_index];

				let new_state: RSVPOptionsState = {
					current_preset: current_preset,
					original_current_preset: clonePreset(current_preset),
					presets: new_preset_list,
					saving_state: this.state.saving_state,
				};

				this.setState(new_state);
				this.props.updateFunc(new_state.current_preset);
			}
		});
	}

	RenderDropdown() {
		if (this.props.is_edit) {
			return;
		}

		let is_default_option = Boolean(
			Constants.WEB_UI_DEFAULT_REACTION_PRESETS.find(
				(value) => value.name === this.state.current_preset.name
			)
		);

		let current_index = this.state.presets.findIndex((value) => {
			return this.state.current_preset.name === value.name;
		});

		let add_preset_tooltip = "Add new preset";
		let save_preset_tooltip = "Save as new preset";
		let delete_preset_tooltip = "Delete preset";

		if (!this.props.is_premium) {
			add_preset_tooltip = "Premium required to edit RSVP options.";
			save_preset_tooltip = "Premium required to edit RSVP options.";
		}

		if (this.props.is_premium && is_default_option) {
			delete_preset_tooltip = "Cannot delete default preset";
		}

		const button_class_names: string[] = [
			"button dashboard-primary-button--tiny mx-1",
		];

		if (this.state.saving_state) {
			button_class_names.push("input-disabled");
		}

		const save_preset_button_class_names = button_class_names.slice();
		const delete_preset_button_class_names = button_class_names.slice();

		if (this.state.saving_state) {
			switch (this.state.saving_state) {
				case "Deleting":
					delete_preset_button_class_names.push("is-loading");
					break;
				case "Saving":
					save_preset_button_class_names.push("is-loading");
					break;
			}
		}

		const options = this.renderPresetDropdown();
		const flat_options = options.flatMap((val) =>
			val.options ? val.options : val
		);

		const selected_option = flat_options.find(
			(val) => val.value === current_index
		);

		return (
			<div>
				<label>Preset</label>
				<div className="control flex flex-col gap-2 sm:flex-row sm:items-center">
					<div className="control">
						<Select
							classNamePrefix="sesh-dropdown"
							value={selected_option}
							options={this.renderPresetDropdown()}
							onChange={this.handlePresetChange}
						></Select>
					</div>
					<div className="flex gap-1">
						<button
							disabled={
								!this.props.is_premium ||
								Boolean(this.state.saving_state)
							}
							onClick={this.onAddPresetPressed}
							className={button_class_names.join(" ")}
							title={add_preset_tooltip}
						>
							<span className="icon is-medium">
								<i className="fa fa-plus"></i>
							</span>
						</button>
						<button
							disabled={
								!this.props.is_premium ||
								Boolean(this.state.saving_state)
							}
							onClick={this.onSavePresetPressed}
							className={save_preset_button_class_names.join(" ")}
							title={save_preset_tooltip}
						>
							<span className="icon is-medium">
								<i className="fa fa-save"></i>
							</span>
						</button>
						<button
							disabled={
								!this.props.is_premium ||
								Boolean(this.state.saving_state) ||
								is_default_option
							}
							onClick={this.onDeletePresetPressed}
							className={delete_preset_button_class_names.join(
								" "
							)}
							style={{ display: "inline-block" }}
							title={delete_preset_tooltip}
						>
							<span className="icon is-medium">
								<i className="fa fa-trash"></i>
							</span>
						</button>
					</div>
					{this.state.current_preset.unsaved_changes && (
						<p style={{ color: "white" }}>Unsaved Preset Changes</p>
					)}
				</div>
			</div>
		);
	}

	RSVPOption(index) {
		let custom_option = this.state.current_preset.custom_options[index];

		let emoji_enabled = false;
		if (this.props.is_edit) {
			emoji_enabled = custom_option.just_added;
		} else {
			emoji_enabled = this.props.is_premium;
		}

		let key = index + custom_option.emoji;

		return (
			<BoxItem
				key={key}
				bgColor="semidark"
				className="flex flex-col gap-2"
			>
				<div className="flex">
					<div className="flex flex-col gap-2 sm:flex-row">
						<div className="tooltip w-1/5">
							{this.OnEditEmojiTooltip(custom_option)}
							<Suspense
								fallback={
									<div className="control">
										Emoji{" "}
										<div>
											<ClipLoader
												size={"1rem"}
												loading={true}
												color={Constants.EMBED_COLOR}
											/>
										</div>
									</div>
								}
							>
								<EmojiPickerForm
									updateFunc={(val) => {
										let event = {
											target: {
												name: "emoji",
												value: val.emoji,
											},
										};
										let second_event = {
											target: {
												name: "custom_emoji_data",
												value: val.custom_emoji_data,
											},
										};
										let third_event = {
											target: {
												name: "guild_id",
												value: val.guild_id,
											},
										};
										this.handleChange(index, event, [
											second_event,
											third_event,
										]);
									}}
									emoji={custom_option.emoji}
									disabled={!emoji_enabled}
									custom_emojis={this.props.custom_emojis}
									all_guild_data={this.props.all_guild_data}
								></EmojiPickerForm>
							</Suspense>
						</div>
						<div className="">
							Name
							<TextInputForm
								key={key}
								initial_value={custom_option.name}
								disabled={!this.props.is_premium}
								char_limit={
									Constants.EVENT_REACTION_OPTION_NAME_LIMIT
								}
								updateFunc={(val) => {
									let event = {
										target: { name: "name", value: val },
									};
									this.handleChange(index, event);
								}}
								placeholder={"Ex: RSVP"}
							></TextInputForm>
						</div>
						<div className="limit-form">
							Limit
							<NumberInputForm
								name="limit"
								initial_value={custom_option.limit}
								do_not_update_initially={true}
								disabled={!this.props.is_premium}
								updateFunc={(val) => {
									let event = {
										target: { name: "limit", value: val },
									};
									this.handleChange(index, event);
								}}
							></NumberInputForm>
						</div>
					</div>
					<div className="ml-auto">
						<DashboardUtils.DeleteButton
							className="is-pulled-right"
							onClick={() => this.onDelete(index)}
						/>
					</div>
				</div>
				<div className="flex gap-4">
					<div className="w-fit">
						<div className="flex flex-wrap gap-2">
							<div className="w-fit">
								<label>
									<input
										name="has_reminders"
										type="checkbox"
										checked={custom_option.has_reminders}
										disabled={!this.props.is_premium}
										onChange={(event) => {
											this.handleChange(index, event);
										}}
									/>{" "}
								</label>
							</div>
							<div className="w-fit">
								<span>Allow Reminders</span>
							</div>
						</div>
					</div>
					<div className="w-fit">
						<div className={`flex flex-wrap gap-2`}>
							<div className="w-fit">
								<input
									name="do_not_apply_attendee_role"
									type="checkbox"
									disabled={!this.props.is_premium}
									checked={
										!Boolean(
											custom_option.do_not_apply_attendee_role
										)
									}
									onChange={(event) => {
										event.target.checked =
											!event.target.checked;
										this.handleChange(index, event);
									}}
								/>{" "}
							</div>
							<div className="flex items-center gap-2">
								<span>Is Attendee</span>
								<InfoTooltip
									info={
										"Indicates if users selecting this option are considered attendees. Users RSVPing to this option will be given Attendee Roles (if enabled) and will auto join Event Threads (if enabled)."
									}
								/>
							</div>
						</div>
					</div>
				</div>
			</BoxItem>
		);
	}

	AddOptionButton(index) {
		let list_type = index % 2 === 0 ? "list-odd" : "list-odd";
		let class_names = `${list_type}`;

		let disabled =
			!this.props.is_premium ||
			this.state.current_preset.custom_options.length >=
				Constants.EVENT_REACTION_LIMIT;

		let button_text;
		if (
			this.state.current_preset.custom_options.length >=
			Constants.EVENT_REACTION_LIMIT
		) {
			button_text = "Max Signup Option Reached";
		} else {
			button_text = "Add Signup Option";
		}
		return (
			<BoxItem
				key="add"
				bgColor="semidark"
				className="flex flex-col items-center"
			>
				<ModifySettingsButton
					hide_arrow
					disabled={disabled}
					onClick={this.onButtonPressed}
				>
					{button_text}
				</ModifySettingsButton>
			</BoxItem>
		);
	}

	onButtonPressed(event) {
		event.preventDefault();
		event.stopPropagation();

		this.state.current_preset.custom_options.push({
			emoji: "😃",
			has_reminders: true,
			name: "",
			limit: null,
			just_added: true,
			user_ids: undefined,
		});

		this.state.current_preset.unsaved_changes = true;

		this.updateOptions(this.state.current_preset);
	}

	RSVPOptionList() {
		let result = [];
		this.state.current_preset.custom_options.forEach((value, index) => {
			result.push(this.RSVPOption(index));
		});
		result.push(this.AddOptionButton(result.length));

		return (
			<ul className="!mx-0 flex max-h-[500px] flex-col gap-2 overflow-y-auto overflow-x-hidden">
				{result}
			</ul>
		);
	}

	SubscribeToPremium() {
		if (this.props.is_premium) {
			return;
		}
		return (
			<div>
				<br></br>
				<label>
					🔒 Subscribe to{" "}
					<a
						className="locked-link"
						href={Constants.PREMIUM_LINK}
						target="_blank"
					>
						Premium
					</a>{" "}
					to edit RSVP options
				</label>
			</div>
		);
	}

	render() {
		let button_element: JSX.Element;
		if (this.props.save_for_guild_settings_data) {
			const disabled = this.state.current_preset.unsaved_changes;

			button_element = (
				<div>
					<div className="field submit-button">
						<div className="control">
							<div className="columns is-mobile is-vcentered is-multiline">
								{disabled && (
									<div className="column is-full">
										<span className="error-text">
											Must save preset before you can set
											it as default
										</span>
									</div>
								)}
								<div className="column is-narrow">
									<button
										disabled={disabled}
										className="button dashboard-primary-button is-size-4-tablet is-size-5-mobile"
										onClick={() => {
											this.props.save_for_guild_settings_data.on_click_func(
												this.state.current_preset
											);
										}}
									>
										{
											this.props
												.save_for_guild_settings_data
												.text
										}
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			);
		}

		return (
			<div>
				<div className="mt-2 flex flex-col gap-2">
					{this.RenderDropdown()}
					{this.SubscribeToPremium()}
					<SettingsBooleanItem
						disabled={!this.props.is_premium}
						label="Allow Multiple RSVPs"
						is_beta={true}
						description="Allow users to RSVP to multiple options"
						value={
							this.state.current_preset.allow_multiple_rsvps ??
							false
						}
						is_loading={false}
						onChange={(checked) => {
							this.state.current_preset.allow_multiple_rsvps =
								checked;
							this.state.current_preset.unsaved_changes = true;
							this.updateOptions(this.state.current_preset);
						}}
					/>
					<div
						key={this.state.current_preset.name}
						className="flex flex-col gap-4"
					>
						{this.RSVPOptionList()}
					</div>
				</div>
				{button_element}
			</div>
		);
	}
}
